<template>
  <div class="tbl-wrap">
    <div class="tbl">
      <div class="tbl__row tbl__row--header" :style="{ gridTemplateColumns: gridCols }">
        <div
          v-for="(col, i) in columns"
          :key="col.key"
          class="tbl__cell tbl__cell--header"
          :class="[
            { 'is-first': i === 0, 'is-last': i === columns.length - 1 },
            `tbl__cell--align-${colAlign[col.key]}`,
          ]"
          :style="{ height: headerHeight + 'px' }"
        >
          <slot :name="`head-${col.key}`">
            <div class="tbl__header-text">{{ col.label }}</div>
          </slot>
        </div>
      </div>

      <div class="tbl__body">
        <template v-if="rows && rows.length">
          <div
            v-for="row in rows"
            :key="rowKey(row)"
            class="tbl__row"
            :class="[rowClass ? rowClass(row) : '', rowIsSection && rowIsSection(row) ? 'tbl__row--section' : '']"
            :style="{ gridTemplateColumns: gridCols }"
          >
            <div
              v-for="(col, idx) in columns"
              :key="col.key"
              class="tbl__cell"
              :class="[
                { 'tbl__cell--hidden': (rowIsSection && rowIsSection(row) && idx > 0) },
                `tbl__cell--align-${colAlign[col.key]}`,
              ]"
              :style="cellStyle(row, idx)"
            >
              <slot v-if="!(rowIsSection && rowIsSection(row) && idx > 0)" :name="`cell-${col.key}`" :row="row">
                <div class="tbl__text" :class="`tbl__text--align-${colAlign[col.key]}`">{{ row[col.key] }}</div>
              </slot>
            </div>
            <div class="tbl__divider" />
          </div>
        </template>
        <div v-else class="tbl__empty">Нет данных</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DataTable',
  props: {
    columns: { type: Array, required: true },
    rows: { type: Array, required: true },
    rowKey: { type: Function, default: (r) => r.id ?? JSON.stringify(r) },
    headerHeight: { type: Number, default: 56 },
    rowHeight: { type: Number, default: 64 },
    rowClass: { type: Function, default: null },
    rowHeightByRow: { type: Function, default: null },
    rowIsSection: { type: Function, default: null },
  },
  computed: {
    gridCols() {
      // По фидбеку 02–03.09.2026:
      //   • если сумма фикс-колонок < ширины контейнера, справа появлялась «дыра»;
      //   • но растягивать последнюю колонку — плохо, когда это `actions`
      //     (короткие иконки превращаются в огромное поле);
      //   • поэтому автопоиск «tie-breaker»-колонки:
      //     1) явно помеченная `col.flex: true`;
      //     2) иначе — самая широкая px-колонка, кроме последней-actions;
      //     3) fallback — просто последняя.
      const cols = this.columns.map(c => (c.width ? `${c.width}px` : '1fr'))
      const hasFlex = cols.some(c => c === '1fr')
      if (hasFlex || cols.length === 0) return cols.join(' ')

      // 1) явно помеченная
      let stretchIdx = this.columns.findIndex(c => c.flex === true)

      // 2) авто: не берём хвостовую actions-колонку — растягиваем самую широкую из остальных
      if (stretchIdx < 0) {
        const last = this.columns[cols.length - 1]
        const lastIsActions = last?.key === 'actions'
        const candidates = this.columns
          .map((c, i) => ({ i, w: c.width || 0, key: c.key }))
          .filter(x => !(lastIsActions && x.i === cols.length - 1))
        if (candidates.length) {
          candidates.sort((a, b) => b.w - a.w)
          stretchIdx = candidates[0].i
        }
      }

      // 3) fallback — последняя
      if (stretchIdx < 0) stretchIdx = cols.length - 1

      // По ТЗ 03.09.2026: min ставим МЕНЬШЕ width (140px или сам width, что меньше),
      // чтобы при узком контейнере колонка ужималась и не выталкивала соседей
      // под горизонтальный скролл. Так «Состав пограничного наряда» не уезжает
      // за левый край при sidebar 18vw + 1440-viewport.
      const wRaw = this.columns[stretchIdx]?.width || 100
      const minW = Math.min(wRaw, 140)
      cols[stretchIdx] = `minmax(${minW}px, 1fr)`
      return cols.join(' ')
    },
    colAlign() {
      // По умолчанию все колонки центрируются (требование дизайна).
      // Можно переопределить через col.align: 'left' | 'right' | 'center'.
      const out = {}
      this.columns.forEach(col => {
        if (col.align === 'right' || col.align === 'left' || col.align === 'center') {
          out[col.key] = col.align
        } else {
          out[col.key] = 'center'
        }
      })
      return out
    },
  },
  methods: {
    cellStyle(row, idx) {
      const isSection = this.rowIsSection && this.rowIsSection(row)
      const h = this.rowHeightByRow ? this.rowHeightByRow(row) : this.rowHeight
      const style = { height: h ? h + 'px' : 'auto' }
      if (isSection && idx === 0) {
        style.gridColumn = '1 / -1'
      }
      return style
    }
  }
}
</script>

<style scoped>
.tbl-wrap {
  width: 100%;
  overflow-x: auto;
  border-radius: 10px;
  border: 2px solid #6B7280;
  background: #FFFFFF;
  overflow-y: visible;
}

.tbl {
  /* По фидбеку 02.09.2026: было width: max-content — при узкой сумме колонок
     таблица не растягивалась на всю ширину, справа появлялась дыра.
     Теперь всегда 100% контейнера + горизонтальный скролл если сумма шире. */
  width: 100%;
  min-width: max-content;
}

.tbl__row {
  display: grid;
  align-items: stretch;
  position: relative;
}

.tbl__cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden;
  border-right: 1px solid #6B7280;
  border-bottom: 1px solid #6B7280;
  font-size: 16px;
  text-align: center;
  /*
    По ТЗ 03.09.2026: grid-item по-умолчанию min-width: auto — контент
    (напр. <input type=text> с default size≈20 ≈ 150-200px) не даёт клетке
    ужаться до объявленной width. Из-за этого SelectField физически шире
    клетки и вылезает за её границы. `min-width: 0` разрешает
    сжатие до объявленной колонковой ширины.
  */
  min-width: 0;
}
/* Прямой ребёнок ячейки — тоже не растягиваем.
   Оборачиваем виджеты (SelectField, MultiSelectField и т.п.) стилем
   min-width:0 + width:100%; overflow:hidden. */
.tbl__cell > * { min-width: 0; max-width: 100%; }
.tbl__row .tbl__cell:last-child { border-right: none; }
.tbl__row:last-child .tbl__cell { border-bottom: none; }

/* Выравнивание содержимого по типу данных колонки.
   Числовые колонки — справа, текстовые — слева. */
.tbl__cell--align-right { justify-content: flex-end; text-align: right; }
.tbl__cell--align-left  { justify-content: flex-start; text-align: left; }
.tbl__cell--align-center { justify-content: center; text-align: center; }
.tbl__text--align-right { text-align: right; width: 100%; font-variant-numeric: tabular-nums; }
.tbl__text--align-left  { text-align: left;  width: 100%; }
.tbl__text--align-center { text-align: center; width: 100%; }

.tbl__row--header .tbl__cell {
  background: #F3F4F6;
  border-bottom: 2px solid #374151;
  font-weight: 700;
  color: #111827;
}

.tbl__cell--hidden {
  height: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  visibility: hidden;
}

.tbl__text {
  flex: 1 1 0;
  color: #111827;
  font-size: 16px;
  font-family: 'Times New Roman', Times, serif;
  font-weight: 400;
  line-height: 20px;
}

.tbl__cell--header .tbl__header-text {
  flex: 1 1 0;
  display: block;
  white-space: normal;     /* разрешаем перенос по словам */
  color: #687588;
  font-size: 14px;
  font-family: Arial, Helvetica, sans-serif;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.2px;
}
.tbl__cell--header.tbl__cell--align-left   .tbl__header-text { text-align: left; }
.tbl__cell--header.tbl__cell--align-right  .tbl__header-text { text-align: right; }
.tbl__cell--header.tbl__cell--align-center .tbl__header-text { text-align: center; }

.tbl__cell--header.is-first {
  border-top-left-radius: 10px;
}

.tbl__cell--header.is-last {
  border-top-right-radius: 10px;
}

.tbl__divider {
  display: none;
}

.tbl__empty {
  padding: 20px;
  text-align: center;
  color: #687588;
  font-size: 12px;
}
</style>
